import React, { forwardRef } from 'react';
import { ChevronDown, AlertCircle } from 'lucide-react';
import styles from './Select.module.css';

/**
 * Reusable Select Component
 *
 * @param {Object} props
 * @param {string} props.label - Select label
 * @param {Array} props.options - Array of {value, label} objects
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text below select
 * @param {boolean} props.required - Required field indicator
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.fullWidth - Full width select
 * @param {string} props.size - 'sm' | 'md' | 'lg'
 * @param {string} props.className - Additional CSS classes
 */
const Select = forwardRef(({
  label,
  options = [],
  placeholder = 'Select an option',
  error,
  helperText,
  required = false,
  disabled = false,
  fullWidth = false,
  size = 'md',
  className = '',
  id,
  name,
  value,
  onChange,
  onBlur,
  ...rest
}, ref) => {
  const selectId = id || name || `select-${Math.random().toString(36).substr(2, 9)}`;

  const wrapperClasses = [
    styles.wrapper,
    fullWidth && styles.fullWidth,
    className
  ].filter(Boolean).join(' ');

  const selectContainerClasses = [
    styles.selectContainer,
    styles[size],
    error && styles.hasError,
    disabled && styles.disabled
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <label htmlFor={selectId} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={selectContainerClasses}>
        <select
          ref={ref}
          id={selectId}
          name={name}
          disabled={disabled}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          className={styles.select}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {error ? (
          <span className={styles.iconRight}>
            <AlertCircle size={18} className={styles.errorIcon} />
          </span>
        ) : (
          <span className={styles.iconRight}>
            <ChevronDown size={18} />
          </span>
        )}
      </div>

      {error && (
        <p id={`${selectId}-error`} className={styles.errorText}>
          {error}
        </p>
      )}

      {!error && helperText && (
        <p id={`${selectId}-helper`} className={styles.helperText}>
          {helperText}
        </p>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
